<template>
  <div class="fin-onboarding">
    <router-view v-if="$route.fullPath === '/onboarding/fasttrack'"/>

    <v-app v-else-if="onboardingMode" id="app" class="public-app">
      <v-main style="background-color: #fff;">
        <FinOnboardingProcess
          :createPortfolioError="createPortfolioError"
          :create-portfolio="createPortfolio"
          @goToMyFinHub="$router.push({name : 'myFinHub'})"
          v-model="model"
          v-if="model.currentState"
        />
        <fin-cookie-declaration/>
      </v-main>

      <fin-alert/>
      <fin-confirm/>
      <fin-lexicon-overlay/>
    </v-app>

    <div v-else style="background-color: #fff;">
      <FinOnboardingProcess
        v-model="model"
        :origin="origin"
        :createPortfolioError="createPortfolioError"
        @errorShown="this.createPortfolioError = false"
        :create-portfolio="createPortfolio"
        @goToMyFinHub="$router.push({name : 'myFinHub'})"
        v-if="model.currentState"
      />
    </div>
  </div>
</template>

<script>
import FinLexiconOverlay from '@/components/common/lexicon/LexiconOverlay'
import FinConfirm from '../../components/common/confirm'
import FinAlert from '../../components/common/alert'
import FinOnboardingProcess from '@/components/pages/onboarding'
import AssetClassFinder from '@/components/pages/onboarding/AssetClassFinder'
import {mapActions, mapGetters, mapState} from 'vuex'
import {finPortfolioAPI} from '@/js/finAPI'
import FinCookieDeclaration from "@/components/common/FinCookieDeclaration"
import {API, Auth, graphqlOperation} from "@/plugins/finAmplifyModules"
import {getBafinDocArchetypeClassifications} from "@/js/finQueries"
import {createUserDocument} from "@/js/finMutations"
import {ERROR_TYPE, handleError} from '@/js/errorhandler/finAWSErrorHandler'

export default {
  name: 'finOnboardingIndex',
  props: {
    origin: {
      type: String
    }
  },
  data() {
    return {
      portfolioId: null,
      model: {},
      onboardingMode: true,
      createPortfolioError: false
    }
  },
  components: {
    FinCookieDeclaration,
    FinOnboardingProcess,
    FinAlert,
    FinConfirm,
    FinLexiconOverlay
  },
  methods: {
    ...mapActions('archetype', ['loadArchetypes']),
    ...mapActions('auth', ['updateOnboarding']),
    createPortfolio(portfolioName) {
      this.createPortfolioError = false
      return new Promise((resolve, reject) => {
        const initialCapital = this.model.step2.initialCapital
        const initialValue = Object.values(this.model.step3.assignedCapital).reduce((pv, cv) => pv + cv, 0)
        console.log("this.model.step3.selectedInstruments", this.model.step3.selectedInstruments)
        let instrumentShare = this.model.step3.selectedInstruments?.SHARE || []
        let instrumentShareEtf = this.model.step3.selectedInstruments?.SHARE_ETF || []
        let instrumentCorporateBondsEtf = this.model.step3.selectedInstruments?.CORPORATE_BONDS_ETF || []
        let instrumentGovernmentBondsEtf = this.model.step3.selectedInstruments?.GOVERNMENT_BONDS_ETF || []
        let instrumentCommoditiesEtf = this.model.step3.selectedInstruments?.COMMODITIES_ETF || []

        let shareEntries = []
        let etfEntries = []

        instrumentShare.forEach((inst) => {
          shareEntries.push({
            count: inst.count,
            averagePrice: inst.instrument.latestPrice.price,
            portfolioShareEntryInstrumentId: inst.instrument.id
          })
        })

        instrumentShareEtf.forEach((inst) => {
          etfEntries.push({
            assetClass: "SHARE_ETF",
            count: inst.count,
            averagePrice: inst.instrument.latestPrice.price,
            portfolioEtfEntryInstrumentId: inst.instrument.id
          })
        })

        instrumentCorporateBondsEtf.forEach((inst) => {
          etfEntries.push({
            assetClass: "CORPORATE_BONDS_ETF",
            count: inst.count,
            averagePrice: inst.instrument.latestPrice.price,
            portfolioEtfEntryInstrumentId: inst.instrument.id
          })
        })

        instrumentGovernmentBondsEtf.forEach((inst) => {
          etfEntries.push({
            assetClass: "GOVERNMENT_BONDS_ETF",
            count: inst.count,
            averagePrice: inst.instrument.latestPrice.price,
            portfolioEtfEntryInstrumentId: inst.instrument.id
          })
        })

        instrumentCommoditiesEtf.forEach((inst) => {
          etfEntries.push({
            assetClass: "COMMODITIES_ETF",
            count: inst.count,
            averagePrice: inst.instrument.latestPrice.price,
            portfolioEtfEntryInstrumentId: inst.instrument.id
          })
        })

        let shareStrategyId = this.archetype?.mainStrategyShare?.id

        finPortfolioAPI.createPortfolio(portfolioName,
          initialCapital,
          initialValue,
          shareEntries,
          etfEntries,
          shareStrategyId,
          this.shareETFStrategyId,
          this.corporateBondsETFStrategyId,
          this.governmentBondsETFStrategyId,
          this.commoditiesETFStrategyId,
          this.assetAllocationTarget,
          true)
          .then(created => {
            const id = created.id
            resolve(id)

            const instruments = this.buildInstrumentsArrayForBafinDocs(instrumentShare, instrumentShareEtf, instrumentCorporateBondsEtf,
              instrumentGovernmentBondsEtf, instrumentCommoditiesEtf)

            if (this.model.step1.archeType) {
              const selectedArchetype = this.archetypeFinder.byArchetypeId(this.model.step1.archeType)

              if (selectedArchetype) {
                const archetClassific = selectedArchetype.classification
                API.graphql(graphqlOperation(getBafinDocArchetypeClassifications)).then(result => {
                  const classifications = result.data.searchArchetypeClassifications.items

                  const anlageZiel = classifications.find(c => c.name === 'investmentObjective').values[archetClassific['investmentObjective']].tooltip.title
                  const anlageDauer = classifications.find(c => c.name === 'investmentHorizon').values[archetClassific['investmentHorizon']].tooltip.title
                  const risikoToleranz = classifications.find(c => c.name === 'attitudeToRisk').values[archetClassific['attitudeToRisk']].tooltip.title

                  this.createBafinDocForPortfolioWithInvestmentAdvice(id, portfolioName, instruments,
                    anlageZiel, anlageDauer, risikoToleranz)
                }).catch(error =>
                  console.error("Onboarding: error retrieveing archetype classifications for bafin docs", error)
                )
              } else {
                console.error("Onboarding: step 3 no archetype found by id " + this.model.step1.archeType)
              }
            } else {
              console.error("Onboarding: step 3 is called without step1.archeType being set, skipping createBafinDocForPortfolioWithInvestmentAdvice")
            }
          }).catch(e => {
          this.createPortfolioError = true
          console.error("Failed to create portfolio", e, e.errors[0].message, typeof e.errors[0].message)
          const error = handleError(ERROR_TYPE.COMMON, e.errors[0])
          const msg = error.msg
          this.$toasted.global.finToast({
            message: msg,
            type: 'error'
          })
        })
      })
    },
    buildInstrumentsArrayForBafinDocs(instrumentShare, instrumentShareEtf, instrumentCorporateBondsEtf,
                                      instrumentGovernmentBondsEtf, instrumentCommoditiesEtf) {
      // Shares always have SRRI 4
      // Etfs have SRRI 2 to 5 depending on their strategies
      const srriMapping = [
        {strategy: 'SHARE', srri: 4},
        {strategy: 'ETF_COMMODITIES_CONSERVATIVE', srri: 2},
        {strategy: 'ETF_COMMODITIES_BASIC', srri: 3},
        {strategy: 'ETF_COMMODITIES_DYNAMIC', srri: 4},
        {strategy: 'ETF_CORPORATE_BONDS_CONSERVATIVE', srri: 3},
        {strategy: 'ETF_CORPORATE_BONDS_BASIC', srri: 4},
        {strategy: 'ETF_CORPORATE_BONDS_DYNAMIC', srri: 5},
        {strategy: 'ETF_EQUITY_CONSERVATIVE', srri: 3},
        {strategy: 'ETF_EQUITY_BASIC', srri: 4},
        {strategy: 'ETF_EQUITY_DYNAMIC', srri: 5},
        {strategy: 'ETF_GOVERNMENT_BONDS_CONSERVATIVE', srri: 3},
        {strategy: 'ETF_GOVERNMENT_BONDS_BASIC', srri: 4},
        {strategy: 'ETF_GOVERNMENT_BONDS_DYNAMIC', srri: 5}
      ]

      const instruments = []
      instrumentShare.forEach((instr) => {
        instruments.push({
          name: instr.instrument.name,
          amount: this.$options.filters.currency(instr.instrument.latestPrice.price * instr.count, '€', 2),
          srri: srriMapping.find(i => i.strategy === 'SHARE').srri
        })
      })
      instrumentShareEtf.forEach((instr) => {
        instruments.push({
          name: instr.instrument.name,
          amount: this.$options.filters.currency(instr.instrument.latestPrice.price * instr.count, '€', 2),
          srri: srriMapping.find(i => i.strategy === this.shareETFStrategyId).srri
        })
      })
      instrumentCorporateBondsEtf.forEach((instr) => {
        instruments.push({
          name: instr.instrument.name,
          amount: this.$options.filters.currency(instr.instrument.latestPrice.price * instr.count, '€', 2),
          srri: srriMapping.find(i => i.strategy === this.corporateBondsETFStrategyId).srri
        })
      })
      instrumentGovernmentBondsEtf.forEach((instr) => {
        instruments.push({
          name: instr.instrument.name,
          amount: this.$options.filters.currency(instr.instrument.latestPrice.price * instr.count, '€', 2),
          srri: srriMapping.find(i => i.strategy === this.governmentBondsETFStrategyId).srri
        })
      })
      instrumentCommoditiesEtf.forEach((instr) => {
        instruments.push({
          name: instr.instrument.name,
          amount: this.$options.filters.currency(instr.instrument.latestPrice.price * instr.count, '€', 2),
          srri: srriMapping.find(i => i.strategy === this.commoditiesETFStrategyId).srri
        })
      })

      return instruments
    },
    createBafinDocForPortfolioWithInvestmentAdvice(userDocumentPortfolioId, portfolioName, instruments,
                                                   anlageZiel, anlageDauer, risikoToleranz) {
      Auth.currentUserCredentials().then(data => {
        const identityId = data.identityId
        const userEmail = this.user.attributes.email
        const userFirstname = this.profile.firstname
        const userLastname = this.profile.lastname
        const ortUndDatum = new Date().toLocaleDateString()
        const signature = userFirstname && userLastname ? `${userFirstname} ${userLastname}` : userEmail.substring(0, userEmail.indexOf('@'))

        // Handling if more than 5 instruments => several files, each of them with 5 instruments
        const instrumentsSplittedArray = []
        while (instruments.length > 0) {
          instrumentsSplittedArray.push(instruments.splice(0, 5))
        }
        console.log("instrumentsSplittedArray", instrumentsSplittedArray)
        instrumentsSplittedArray.forEach((arrayOfInstruments, index) => {
          const nr = index + 1
          const filename = "12_Geeignetheitserklaerung_" + userDocumentPortfolioId + "_" + nr + ".pdf"
          const comment = "Geeignetheitserklärung - Portfolio " + portfolioName +
            (instrumentsSplittedArray.length > 1 ? ' (Teil ' + nr + ")" : '')
          const documentId = "bafin12_" + userDocumentPortfolioId + "_" + nr
          this.createBafinDoc(identityId, userDocumentPortfolioId, documentId, "bafin12", filename, comment, ortUndDatum, signature, arrayOfInstruments,
            anlageZiel, anlageDauer, risikoToleranz)
        })
      })
    },
    createBafinDoc(userIdentityId, userDocumentPortfolioId, documentId, formId, fileName, comment, ortUndDatum, signature, instruments,
                   anlageZiel, anlageDauer, risikoToleranz) {
      const version = 1
      const acceptRequired = true
      let input = undefined
      const fieldValues = [
        {name: "OrtDatum", value: ortUndDatum},
        {name: "Unterschrift", value: signature},
        {name: "Anlageziel", value: anlageZiel},
        {name: "Anlagedauer", value: anlageDauer},
        {name: "Risikotoleranz", value: risikoToleranz}
      ]
      instruments.forEach((item, index) => {
        fieldValues.push({name: "Produkt" + (index + 1), value: item.name})
        fieldValues.push({name: "Betrag" + (index + 1), value: item.amount})
        fieldValues.push({name: "SRRI" + (index + 1), value: item.srri})
      })
      input = {
        input: {
          acceptRequired: acceptRequired,
          comment: comment,
          documentId: documentId,
          formId: formId,
          fileName: fileName,
          userDocumentPortfolioId: userDocumentPortfolioId,
          userIdentityId: userIdentityId,
          version: version,
          fieldValues: fieldValues
        }
      }
      console.log("createUserRegistrationDocuments input", input)
      return new Promise((resolve, reject) => {
        API.graphql({
          query: createUserDocument,
          variables: input
        })
          .catch(reject => {
            console.error("reject", reject)
          })
      })
    },
    getEtfStrategyModelID(assetClass) {
      const models = this.archetype?.etfStrategyModels?.items
      if (models) {
        return models.find(m => m.etfStrategyModel?.assetClass === assetClass)?.strategyModelId
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['userOnboardingData', 'userId', 'onboardingArchetypeID', 'profileArchetype', 'profile', 'user']),
    ...mapState('archetype', ['loadingArchetypes']),
    ...mapGetters('archetype', ['archetypeFinder']),
    archetype() {
      let archetypeID = this.onboardingArchetypeID

      // if called via finStrategist = Onboarding Step 2 + 3, use the previously saved profile archetype
      if (this.$route.fullPath.includes('finStrategist')) {
        archetypeID = this.profileArchetype?.id
      }

      return this.isLoading ? null : this.archetypeFinder?.byArchetypeId(archetypeID)
    },
    isLoading() {
      return this.loadingArchetypes
    },
    assetAllocationTarget() {
      return AssetClassFinder.all.map((val, i) => {
        const assetAlloc = this.model.step2.assetAllocation[i]
        if (!assetAlloc) return null

        console.log("assetAllocationTarget result", val.id + " - " + assetAlloc.percentage)
        return {
          name: val.id,
          percentage: assetAlloc.percentage
        }
      }).filter(item => item)
    },
    shareETFStrategyId() {
      return this.getEtfStrategyModelID("SHARE_ETF")
    },
    corporateBondsETFStrategyId() {
      return this.getEtfStrategyModelID("CORPORATE_BONDS_ETF")
    },
    governmentBondsETFStrategyId() {
      return this.getEtfStrategyModelID("GOVERNMENT_BONDS_ETF")
    },
    commoditiesETFStrategyId() {
      return this.getEtfStrategyModelID("COMMODITIES_ETF")
    },
  },
  mounted() {
    this.model = this.userOnboardingData

    // userOnboardingData is returning an empty object if onboarding hasn't been started => check for currentState
    if (!this.model.currentState) {
      this.model.currentState = 1
    }

    this.loadArchetypes()

    if (this.$route.fullPath.includes('finProfiler') || this.$route.fullPath.includes('finStrategist')) {
      this.onboardingMode = false
    }
  },
  watch: {
    model: {
      handler(val) {
        // todo then catch and loading state
        const jsonVal = JSON.stringify(val) || '{}'
        if (jsonVal !== "{}") {
          this.updateOnboarding(jsonVal).then(data => {
            // this.$toasted.clear()
            // this.$toasted.show('Deine Änderung wurde gespeichert', {
            //   type: 'success',
            //   icon: 'icon-notification-success',
            //   duration: 3000
            // })
          })
            .catch(e => {
              console.error('updateOnboarding failed', e)
              this.$toasted.global.finToast({
                message: 'Beim Speichern ist ein Fehler aufgetreten.',
                type: 'error'
              })
            })
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.fin-onboarding {
  height: inherit;
}
</style>
